import React, { useEffect } from 'react'
import cn from 'classnames'
import { withRouter } from 'react-router-dom'
import { get, isEmpty } from 'lodash'

import { getOrderInProgress, makeGetOrder } from '../selectors/order'
import ReceiptCard from '../components/receipt/ReceiptCard'
import QRCodeReceipt from '../containers/receipt/QRCodeReceipt'
import { clearCart } from '../actions/cart'
import { clearPromotionsOnOrder } from '../actions/promotion'
import { ReactComponent as CloseIcon } from '../assets/closeMobile.svg'
import { isEmpty as vnIsEmpty } from '../VNUtil/VNUtils'

import styles from './Receipt.module.scss'
import { getIsKiosk } from '../selectors/menus'
import { getActiveRevenueCenter } from '../selectors/revenueCenter'
import { makeGetServiceChargeLegalText } from '../selectors/venue'
import queryString from 'query-string'

import { getDeviceMode } from '../VNMode/Selectors'
import { MODES } from '../VNMode/Reducer'
import { sendCFDScreenNav } from '../VNAndroidSDK/bridgeCalls/VNWebSDKDataSend'
import { CFD_SCREEN } from '../VNCustomerFacingDisplay/Reducer'
import { getCurrentPaymentFlow } from '../VNCustomerFacingDisplay/Selectors'
import { CFD_POS_PAYMENT_FLOWS } from '../VNCustomerFacingDisplay/Enums'
import { setCustomerPayingWithCard, setCustomerViewingReceiptDialog } from '../VNDialogs/ActionCreators'
import { customHistory } from '../stores/index'
import { useSelector, useDispatch, connect } from 'react-redux'
import { makeStyles } from '@material-ui/core'
import { setBalanceToBePaid } from '../VNCustomerFacingDisplay/ActionCreators'
import VNConcessionsNavBar from '../components/VNConcessionsNavBar'
import useGetIsMobileScreen from '../hooks/useGetIsMobileScreen'
import { handlePpiReportOrder } from '../VNPPI/Utils'
import { getPpiConfigurations, getPpiOrderDetails } from '../VNPPI/Selectors'
import { getActiveAttendantId } from '../selectors/attendant'
import { isTabbed } from '../utils/orderStates'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import useSpinner from '../context/hooks/useSpinner'
import { getReceiptSettings } from '../selectors/config'
import { getPrintingEnabled } from '../selectors/printer'
import usePrinter from '../VNPrinters/hooks/usePrinter'

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100%',
    width: '100%',
    backgroundColor: '#000000',
  },
}))

const Receipt = ({
  className,
  order,
  orderNumber,
  isRichCheckoutOrder,
  serviceChargeLegal,
  hasBalanceDueInCents,
  isKiosk,
  ppiConfigurations,
  ppiOrderDetails,
  activeAttendantId,
  activeRevenueCenter,
  isSplitTender,
  isNewOrder,
}) => {
  const dispatch = useDispatch()
  const history = useHistory()
  // SELECTORS
  // what mode are we in
  const { stopSpinner } = useSpinner()
  const cfdMode = useSelector((state) => getDeviceMode(state))
  const currentPaymentFlow = useSelector((state) => getCurrentPaymentFlow(state))
  const receiptSettings = useSelector((state) => getReceiptSettings(state))
  const printingEnabled = useSelector((state) => getPrintingEnabled(state))
  const queryStrings = queryString.parse(window?.location?.search, { arrayFormat: 'comma' })
  const { printReceipt } = usePrinter(order?.uuid)

  // apply receipt styles for cfd
  const cfdStyles = useStyles()

  // navigate to the receipt screen if we are in CFD mode
  useEffect(() => {
    stopSpinner()

    if (isEmpty(order)) return
    if (cfdMode !== MODES.POS) return
    if (parseInt(order.balanceDueInCents) > 0 && queryStrings?.origin !== 'orderDetails') return
    const isTabs = order.isTab || isTabbed(order)
    const shouldShowReceipt =
      isTabs ||
      currentPaymentFlow !== CFD_POS_PAYMENT_FLOWS.CREDIT_CARD_AUTHORIZATION ||
      currentPaymentFlow !== CFD_POS_PAYMENT_FLOWS.CUSTOM_TENDER ||
      queryStrings?.origin === 'orderDetails'

    if (currentPaymentFlow === CFD_POS_PAYMENT_FLOWS.SPLIT_PAY) {
      dispatch(setCustomerPayingWithCard({ open: false }))
    }

    if (shouldShowReceipt) {
      dispatch(setCustomerViewingReceiptDialog({ open: true, uuid: order?.uuid }))
      if (history?.location?.state?.isRichCheckout) return

      sendCFDScreenNav(CFD_SCREEN.RECEIPT, {
        cartTotals: { uuid: order?.uuid, remainingTicketBalance: order?.remainingTicketBalance },
        transaction: { amount: order?.amountInCents ? order?.amountInCents / 100 : 0 },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order])

  // auto print the receipt if the order is not from order details
  // only for vanilla pos
  useEffect(() => {
    if (cfdMode === MODES.POS) return
    if (!printingEnabled) return
    if (queryStrings?.origin === 'orderDetails') return

    if (!hasBalanceDueInCents) {
      printReceipt(isSplitTender, isNewOrder, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiptSettings.autoPrintReceipt])

  useEffect(() => {
    if (cfdMode !== MODES.POS) return
    if (queryStrings?.origin === 'orderDetails') return
    const balanceDueInCents = parseInt(order?.balanceDueInCents || 0)
    if ((isTabbed(order) || order.isTab) && balanceDueInCents <= 0) return
    // if the order is rich checkout then we do not offer split payments.
    if (order?.isRichCheckout) return

    if (balanceDueInCents > 0) {
      let totalAmountPaid = 0
      order.payments.forEach((payment) => {
        totalAmountPaid += payment.amountInCents
      })

      const balanceToBePaid = {
        amountPaid: totalAmountPaid,
        balanceDue: balanceDueInCents ?? 0,
      }

      dispatch(setBalanceToBePaid(balanceToBePaid))
      sendCFDScreenNav(CFD_SCREEN.BALANCE_DUE, { balanceToBePaid: balanceToBePaid })

      customHistory.push({
        pathname: '/pos/balance-due',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onDone = () => {
    const posInfo = {
      venueId: process.env.REACT_APP_VENUE_UUID,
      userId: activeAttendantId,
      vendorName: activeRevenueCenter.name,
      deviceId: window.localStorage.device_id,
    }

    if (hasBalanceDueInCents) {
      customHistory.push({
        pathname: '/split-tender',
        state: { order },
      })

      return
    }

    if (!vnIsEmpty(order) && !vnIsEmpty(ppiOrderDetails) && !vnIsEmpty(ppiConfigurations) && !vnIsEmpty(activeRevenueCenter)) {
      handlePpiReportOrder(order, ppiConfigurations, ppiOrderDetails, posInfo, activeRevenueCenter.customerAttributes?.ppiExtras)
    }
    sendCFDScreenNav(CFD_SCREEN.IDLE)

    dispatch(clearCart())
    dispatch(clearPromotionsOnOrder())
    customHistory.push('/concession-order')
  }

  const classNames = cn(styles.receipt, className)
  const isMobile = useGetIsMobileScreen()

  const richCheckoutDisclaimer = isRichCheckoutOrder ? (
    <div className={styles.disclaimer}>
      Discounts and gratuity applied from mobile purchases are not included. Please check your mobile receipt for final total.
    </div>
  ) : null

  const serviceChargeDisclaimer =
    !isKiosk && serviceChargeLegal ? <div className={styles.disclaimer}>{serviceChargeLegal}</div> : null

  const display = () => {
    if (cfdMode === MODES.POS) {
      return <div className={cfdStyles.root}></div>
    } else {
      return (
        <div className={classNames}>
          <VNConcessionsNavBar
            showIcon={isMobile ? true : false}
            useHamburgerIcon={false}
            leftAccessory={<span className={styles.title}>Receipt</span>}
            rightAccessory={<CloseIcon onClick={onDone} />}
          />
          <div className={styles.contentWindow}>
            <div className={styles.receiptContent}>
              <div className={cn(styles.orderNumber, orderNumber && orderNumber.length > 7 && styles.longOrderNumber)}>
                {orderNumber}
              </div>
              <ReceiptCard
                className={styles.receiptCard}
                isRichCheckoutOrder={isRichCheckoutOrder}
                serviceChargeEnabled={serviceChargeLegal}
                isMobile={isMobile}
              />
              {!isMobile && (
                <div className={styles.disclaimer}>
                  {richCheckoutDisclaimer}
                  {serviceChargeDisclaimer}
                </div>
              )}
            </div>
            {!isMobile && <QRCodeReceipt className={styles.buttons} onDone={onDone} />}
            {isMobile && (
              <QRCodeReceipt
                className={cn([styles.buttons, hasBalanceDueInCents && styles.splitReceiptButtons])}
                onDone={onDone}
              />
            )}
          </div>
        </div>
      )
    }
  }

  return display()
}

const mapStateToProps = (state, ownProps) => {
  const orderUuid = get(ownProps, 'match.params.orderUuid', undefined)
  const paymentId = ownProps?.match?.params?.paymentId
  const getOrder = makeGetOrder()
  const isKiosk = getIsKiosk(state)
  const orderInProgress = getOrderInProgress(state)

  let order = {}
  if (!isEmpty(orderInProgress)) {
    order = orderInProgress
  } else if (orderUuid) {
    order = getOrder(state, orderUuid)
  } else {
    order = getOrder(state, state.order?.currentOrderId)
  }

  const hasBalanceDueInCents = paymentId ? order.balanceDueInCents !== 0 : false

  const orderNumber = order?.orderNumber

  const isSplitTender = orderUuid === 'split-tender'
  const isNewOrder = !order?.submittedAt
  const isRichCheckoutOrder = order?.isRichCheckout
  const serviceChargeLegal = order?.serviceFee?.total > 0 && makeGetServiceChargeLegalText()(state)

  // PPI
  const ppiConfigurations = getPpiConfigurations(state)
  const ppiOrderDetails = getPpiOrderDetails(state, order?.uuid)
  const activeAttendantId = getActiveAttendantId(state)
  const activeRevenueCenter = getActiveRevenueCenter(state)

  return {
    order,
    orderNumber,
    isNewOrder,
    isSplitTender,
    isRichCheckoutOrder,
    serviceChargeLegal,
    hasBalanceDueInCents,
    isKiosk,
    ppiConfigurations,
    ppiOrderDetails,
    activeAttendantId,
    activeRevenueCenter,
  }
}

export default withRouter(connect(mapStateToProps, undefined)(Receipt))
